import { useEffect, useRef, useState, type ReactNode } from 'react'
import { MenuDeAparelhos, MenuDeFundo, useNivelDoMicrofone } from '../chat/controles-de-midia.tsx'
import { useChamada, type Quadro } from './chamada.tsx'
import { IconePlanta } from './IconePlanta.tsx'
import '../chat/videochamada.css'
import './reuniao.css'

/**
 * A reunião numa sala do Escritório (SPEC-20261010-0157, SPEC-20261010-1557),
 * no jeito do Meet, dentro do Orbind: o rail com os apps fica à esquerda.
 *
 * É só um jeito de olhar a chamada que já está rodando no casco
 * (`chamada.tsx`): voltar ao tabuleiro ou abrir outra tela não derruba
 * ninguém. Todos em quadros (foto quando a câmera está desligada), quem fala
 * ganha um anel, quem apresenta a tela ganha o palco. Microfone, câmera,
 * apresentar, reações, levantar a mão, chat da reunião e lista de pessoas.
 */

type Painel = 'chat' | 'pessoas' | null
type Menu = { tipo: 'aparelhos' | 'fundo' | 'reacoes'; ancora: HTMLElement } | null

export const EMOJIS = ['👍', '❤️', '😂', '😮', '👏', '🎉', '🙏', '🔥']
const hora = (t: number) => new Date(t).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
const CORES = ['#3f6fe0', '#1f9e8a', '#d9822b', '#8a5be0', '#c2484f', '#2c8fd6', '#4f9d3a', '#d6a21e', '#b0558c']
const corDe = (texto: string) => CORES[[...texto].reduce((a, c) => a + c.charCodeAt(0), 0) % CORES.length] as string
const iniciais = (nome: string) => {
  const p = nome.trim().split(/\s+/)
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toUpperCase()
}

export function Foto({ nome, foto, tamanho }: { nome: string; foto: string | null; tamanho: number }) {
  const [falhou, setFalhou] = useState(false)
  return (
    <span className="rs-foto" style={{ width: tamanho, height: tamanho, background: corDe(nome), fontSize: tamanho * 0.36 }}>
      {foto && !falhou ? <img src={foto} alt="" onError={() => setFalhou(true)} /> : iniciais(nome)}
    </span>
  )
}

/** Vídeo sempre mudo: a voz de todos toca no casco (`chamada.tsx`), não aqui. */
export function Video({ stream, espelhar, className }: { stream: MediaStream | null; espelhar?: boolean; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const v = ref.current
    if (!v) return
    if (v.srcObject !== stream) v.srcObject = stream
    if (stream) void v.play().catch(() => undefined)
  }, [stream])
  return <video ref={ref} className={`${className ?? ''}${espelhar ? ' rs-espelho' : ''}`} autoPlay playsInline muted />
}

export function QuadroDaPessoa({ q, fixado, onFixar, pequeno }: { q: Quadro; fixado: boolean; onFixar?: () => void; pequeno?: boolean }) {
  const nivel = useNivelDoMicrofone(q.mic ? q.audio : null)
  const falando = q.mic && nivel > 0.14
  return (
    <div className={`rs-quadro${falando ? ' rs-quadro--fala' : ''}${pequeno ? ' rs-quadro--pequeno' : ''}`}>
      <Video stream={q.camera} espelhar={q.eu} className={q.video ? 'rs-video' : 'rs-video rs-oculto'} />
      {q.video ? null : (
        <div className="rs-sem-camera">
          <Foto nome={q.nome} foto={q.foto} tamanho={pequeno ? 52 : 96} />
        </div>
      )}
      {q.mao ? (
        <span className="rs-mao" title="Mão levantada">
          ✋
        </span>
      ) : null}
      <span className="rs-estado">
        {q.mic ? (
          <span className={`rs-onda${falando ? ' rs-onda--on' : ''}`} aria-hidden>
            <i style={{ height: 4 + nivel * 10 }} />
            <i style={{ height: 4 + nivel * 16 }} />
            <i style={{ height: 4 + nivel * 10 }} />
          </span>
        ) : (
          <i className="fas fa-microphone-slash" aria-label="Microfone desligado" />
        )}
      </span>
      <span className="rs-nome">{q.eu ? 'Você' : q.nome}</span>
      {onFixar ? (
        <button type="button" className={`rs-fixar${fixado ? ' rs-fixar--on' : ''}`} aria-label={fixado ? 'Soltar do destaque' : 'Fixar em destaque'} title={fixado ? 'Soltar do destaque' : 'Fixar em destaque'} onClick={onFixar}>
          <i className="fas fa-thumbtack" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}

export function Botao({ ativo, perigo, rotulo, icone, desenho, onClick, children, extra }: { ativo?: boolean; perigo?: boolean; rotulo: string; icone: string; desenho?: ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void; children?: ReactNode; extra?: ReactNode }) {
  return (
    <span className="rs-botao-grupo">
      <button type="button" className={`rs-botao${ativo === false ? ' rs-botao--desligado' : ''}${ativo === true ? ' rs-botao--ligado' : ''}${perigo ? ' rs-botao--sair' : ''}`} aria-label={rotulo} title={rotulo} onClick={onClick}>
        {desenho ?? <i className={`fas ${icone}`} aria-hidden />}
        {children}
      </button>
      {extra}
    </span>
  )
}

/** Microfone e câmera com as setinhas de aparelhos e de fundo: a barra do tabuleiro e a da reunião usam os mesmos. */
export function BotoesDeMidia({ onMenu }: { onMenu: (m: Menu) => void }) {
  const ch = useChamada()
  if (!ch) return null
  return (
    <>
      <Botao
        ativo={ch.micLigado ? undefined : false}
        rotulo={ch.micLigado ? 'Desligar microfone' : 'Ligar microfone'}
        icone={ch.micLigado ? 'fa-microphone' : 'fa-microphone-slash'}
        onClick={ch.alternarMic}
        extra={
          <button type="button" className="rs-seta" aria-label="Escolher microfone, câmera e saída de som" onClick={(e) => onMenu({ tipo: 'aparelhos', ancora: e.currentTarget })}>
            <i className="fas fa-chevron-up" aria-hidden />
          </button>
        }
      />
      <Botao
        ativo={ch.camLigada ? undefined : false}
        rotulo={ch.camLigada ? 'Desligar câmera' : 'Ligar câmera'}
        icone={ch.camLigada ? 'fa-video' : 'fa-video-slash'}
        onClick={ch.alternarCamera}
        extra={
          <button type="button" className="rs-seta" aria-label="Fundo da câmera" onClick={(e) => onMenu({ tipo: 'fundo', ancora: e.currentTarget })}>
            <i className="fas fa-magic" aria-hidden />
          </button>
        }
      />
    </>
  )
}

/** Os menus que abrem das setinhas (aparelhos e fundo da câmera). */
export function MenusDeMidia({ menu, onFechar }: { menu: Menu; onFechar: () => void }) {
  const ch = useChamada()
  if (!ch || !menu) return null
  if (menu.tipo === 'aparelhos')
    return <MenuDeAparelhos ancora={menu.ancora} onFechar={onFechar} onTrocarMicrofone={ch.trocarMicrofone} onTrocarCamera={ch.trocarCamera} onTrocarSaida={ch.trocarSaida} faixaDoMicrofone={ch.faixaDoMic} comTeste />
  if (menu.tipo === 'fundo') return <MenuDeFundo ancora={menu.ancora} estilo={ch.estiloFundo} imagem={ch.imagemFundo} onFechar={onFechar} onTrocar={(e, i) => void ch.trocarFundo(e, i)} />
  return null
}
export type { Menu }

export function ReuniaoDaSala({ cenario, onVoltar, onSair }: { cenario?: string; onVoltar: () => void; onSair: () => void }) {
  const ch = useChamada()
  const [painel, setPainel] = useState<Painel>(null)
  const [menu, setMenu] = useState<Menu>(null)
  const [fixado, setFixado] = useState<string | null>(null)
  const [agora, setAgora] = useState(Date.now())
  const [texto, setTexto] = useState('')
  const raiz = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = window.setInterval(() => setAgora(Date.now()), 15_000)
    return () => window.clearInterval(t)
  }, [])
  // o chat aberto aqui zera as não lidas; sair da reunião fecha o chat
  useEffect(() => {
    ch?.abrirChat(painel === 'chat')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painel])
  useEffect(() => () => ch?.abrirChat(false), [])

  if (!ch?.sala) return null
  const { quadros, quemApresenta, sala } = ch

  const abrirPainel = (p: Painel) => setPainel((atual) => (atual === p ? null : p))
  const telaCheia = () => {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void raiz.current?.requestFullscreen().catch(() => undefined)
  }
  const enviar = () => {
    ch.enviarMensagem(texto)
    setTexto('')
  }

  const destaque = quemApresenta ? null : (quadros.find((q) => q.id === fixado) ?? null)
  const colunas = quadros.length <= 1 ? 1 : quadros.length <= 4 ? 2 : quadros.length <= 9 ? 3 : 4
  const pessoas = quadros.length

  return (
    <div
      className={`rsala rsala--embutida${painel ? ' rsala--com-painel' : ''}${cenario ? ' rsala--cenario' : ''}`}
      ref={raiz}
      style={cenario ? { ['--rs-fundo' as string]: `url(${cenario})` } : undefined}
    >
      <main className="rs-palco">
        {quemApresenta ? (
          <div className="rs-apresentacao">
            <div className="rs-tela">
              <Video stream={quemApresenta.tela} className="rs-video rs-video--tela" />
              <span className="rs-nome">
                <i className="fas fa-desktop" aria-hidden /> {quemApresenta.nome === 'Você' ? 'Você está apresentando' : `${quemApresenta.nome} está apresentando`}
              </span>
            </div>
            <div className="rs-tira">
              {quadros.map((q) => (
                <QuadroDaPessoa key={q.id} q={q} pequeno fixado={false} onFixar={() => setFixado(q.id)} />
              ))}
            </div>
          </div>
        ) : destaque ? (
          <div className="rs-apresentacao">
            <div className="rs-tela">
              <QuadroDaPessoa q={destaque} fixado onFixar={() => setFixado(null)} />
            </div>
            <div className="rs-tira">
              {quadros
                .filter((q) => q.id !== destaque.id)
                .map((q) => (
                  <QuadroDaPessoa key={q.id} q={q} pequeno fixado={false} onFixar={() => setFixado(q.id)} />
                ))}
            </div>
          </div>
        ) : (
          <div className="rs-grade" style={{ gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` }}>
            {quadros.map((q) => (
              <QuadroDaPessoa key={q.id} q={q} fixado={false} onFixar={() => setFixado(q.id)} />
            ))}
          </div>
        )}
        {pessoas === 1 ? <p className="rs-sozinho">Só você por aqui. Quem entrar nesta sala aparece aqui.</p> : null}
        <div className="rs-reacoes" aria-hidden>
          {ch.reacoes.map((r) => (
            <span key={r.id} className="rs-reacao" style={{ left: `${r.x}%` }}>
              <b>{r.emoji}</b>
              <small>{r.nome}</small>
            </span>
          ))}
        </div>
        {ch.balao && painel !== 'chat' ? (
          <button type="button" className="rs-balao" onClick={() => abrirPainel('chat')}>
            <b>{ch.balao.nome}</b>
            <span>{ch.balao.texto}</span>
          </button>
        ) : null}
      </main>

      {painel ? (
        <aside className="rs-painel">
          <header>
            <h2>{painel === 'chat' ? 'Mensagens na chamada' : 'Pessoas'}</h2>
            <button type="button" className="rs-fechar" aria-label="Fechar" onClick={() => setPainel(null)}>
              <i className="fas fa-times" aria-hidden />
            </button>
          </header>
          {painel === 'pessoas' ? (
            <ul className="rs-pessoas">
              <li className="rs-pessoas-titulo">Na chamada · {pessoas}</li>
              {quadros.map((q) => (
                <li key={q.id}>
                  <Foto nome={q.nome} foto={q.foto} tamanho={34} />
                  <span>
                    {q.nome}
                    {q.eu ? <small> (você)</small> : null}
                  </span>
                  {q.mao ? <em title="Mão levantada">✋</em> : null}
                  <i className={`fas ${q.mic ? 'fa-microphone' : 'fa-microphone-slash rs-mudo'}`} aria-label={q.mic ? 'Microfone ligado' : 'Microfone desligado'} />
                </li>
              ))}
            </ul>
          ) : (
            <>
              <p className="rs-chat-nota">As mensagens só aparecem para quem está na chamada e somem quando ela termina.</p>
              <div className="rs-chat">
                {ch.mensagens.length ? (
                  ch.mensagens.map((m, i) => (
                    <div key={m.id} className={`rs-msg${m.minha ? ' rs-msg--minha' : ''}`}>
                      {i === 0 || ch.mensagens[i - 1]?.de !== m.de ? (
                        <span className="rs-msg-quem">
                          <b>{m.minha ? 'Você' : m.nome}</b> {hora(m.hora)}
                        </span>
                      ) : null}
                      <p>{m.texto}</p>
                    </div>
                  ))
                ) : (
                  <p className="rs-chat-vazio">Nenhuma mensagem ainda.</p>
                )}
              </div>
              <form
                className="rs-escrever"
                onSubmit={(e) => {
                  e.preventDefault()
                  enviar()
                }}
              >
                <input value={texto} maxLength={2000} placeholder="Enviar mensagem para todos" aria-label="Mensagem para todos na chamada" onChange={(e) => setTexto(e.target.value)} autoFocus />
                <button type="submit" aria-label="Enviar" disabled={!texto.trim()}>
                  <i className="fas fa-paper-plane" aria-hidden />
                </button>
              </form>
            </>
          )}
        </aside>
      ) : null}

      <footer className="rs-barra">
        <div className="rs-barra-info">
          <b>{hora(agora)}</b>
          <span>{sala.nome}</span>
        </div>
        <div className="rs-barra-meio">
          <BotoesDeMidia onMenu={setMenu} />
          <Botao ativo={ch.apresentando ? true : undefined} rotulo={ch.apresentando ? 'Parar de apresentar' : 'Apresentar a tela'} icone="fa-desktop" onClick={() => void ch.apresentar()} />
          <Botao rotulo="Reações" icone="fa-smile" onClick={(e) => setMenu(menu?.tipo === 'reacoes' ? null : { tipo: 'reacoes', ancora: e.currentTarget })} />
          <Botao ativo={ch.mao ? true : undefined} rotulo={ch.mao ? 'Abaixar a mão' : 'Levantar a mão'} icone="fa-hand-paper" onClick={ch.alternarMao} />
          <Botao rotulo="Tela cheia" icone="fa-expand" onClick={telaCheia} />
          <Botao rotulo="Voltar ao tabuleiro (a chamada continua)" icone="" desenho={<IconePlanta tamanho={20} />} onClick={onVoltar} />
          <Botao perigo rotulo="Sair da chamada" icone="fa-phone-slash" onClick={onSair} />
        </div>
        <div className="rs-barra-lado">
          <Botao ativo={painel === 'pessoas' ? true : undefined} rotulo="Pessoas" icone="fa-user-friends" onClick={() => abrirPainel('pessoas')}>
            <span className="rs-conta">{pessoas}</span>
          </Botao>
          <Botao ativo={painel === 'chat' ? true : undefined} rotulo="Mensagens na chamada" icone="fa-comment-alt" onClick={() => abrirPainel('chat')}>
            {ch.naoLidas ? <span className="rs-conta rs-conta--nova">{ch.naoLidas}</span> : null}
          </Botao>
        </div>
      </footer>

      {menu?.tipo === 'reacoes' ? (
        <div className="rs-emojis" role="menu" aria-label="Reações">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              role="menuitem"
              onClick={() => {
                ch.reagir(e)
                setMenu(null)
              }}
            >
              {e}
            </button>
          ))}
        </div>
      ) : (
        <MenusDeMidia menu={menu} onFechar={() => setMenu(null)} />
      )}
      {ch.aviso ? <p className="rs-aviso" role="status">{ch.aviso}</p> : null}
    </div>
  )
}
